import React, { useEffect, useState } from "react";
import { FaCar, FaTools, FaUserTie, FaBox } from "react-icons/fa";
import Select from "react-select";
import {
    getEmployeeForWorkshop,
    getInventoryByWorkshopId, getInventoryForWorkshop,
    getJobCardDetails,
    getWorkTypeByWorkshopId,
    updateJobCardForWorkshopId,
} from "../../Api/Api";
import {useNotification} from "../Notification/NotificationContext";

const EditJobCardModal = ({ show, handleClose, jobCardId, workshopId, onUpdate }) => {
    const [jobCard, setJobCard] = useState(null);
    const [workTypes, setWorkTypes] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [selectedWorkTypes, setSelectedWorkTypes] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedInventory, setSelectedInventory] = useState([]);
    const [inventoryDetails, setInventoryDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { showNotification } = useNotification();

    useEffect(() => {
        if (show && jobCardId) {
            setLoading(true);
            Promise.all([
                getJobCardDetails(jobCardId),
                getWorkTypeByWorkshopId(workshopId),
                getEmployeeForWorkshop(workshopId, 100, 0),
                getInventoryForWorkshop(workshopId,1000,0)
            ])
                .then(([jobCardRes, workTypesRes, employeeRes, inventoryRes]) => {
                    const jc = jobCardRes.data;
                    setJobCard(jc);
                    setWorkTypes(workTypesRes.data);
                    setEmployees(employeeRes.data.content);
                    setInventoryItems(inventoryRes.data);

                    // Pre-populate selections
                    setSelectedWorkTypes(
                        jc.workTypes.map(wt => ({
                            value: wt.id,
                            label: `${wt.name} (₹${wt.price})`
                        }))
                    );

                    setSelectedEmployees(
                        jc.assignedEmployees.map(emp => ({
                            value: emp.id,
                            label: `${emp.name}`
                        }))
                    );

                    const selectedInv = jc.inventoryItems.map(item => ({
                        value: item.id,
                        label: `${item.name} (${item.quantity} pcs)`
                    }));

                    setSelectedInventory(selectedInv);

                    const invDetails = {};
                    jc.inventoryItems.forEach((item) => {
                        invDetails[item.id] = {
                            id:item.id,
                            quantity: item.quantity,
                            underWarranty: item.underWarranty
                        };
                    });
                    setInventoryDetails(invDetails);
                })
                .catch(console.error)
                .finally(() => setLoading(false));
        }
    }, [show, jobCardId, workshopId]);

    const workTypesOptions = workTypes?.map(wt => ({
        value: wt.id,
        label: `${wt.name} (₹${wt.price})`
    }));

    const employeeOptions = employees?.map(emp => ({
        value: emp.id,
        label: `${emp.name}`
    }));

    const inventoryOptions = inventoryItems?.content?.map(item => ({
        value: item.id,
        label: `${item.name} (${item.quantity} in stock)`
    }));

    const handleInventoryChange = (selected) => {
        setSelectedInventory(selected || []);
        setInventoryDetails(prev => {
            const updated = {};
            (selected || []).forEach(item => {
                updated[item.value] = prev[item.value] || {
                    quantity: 1,
                    underWarranty: false
                };
            });
            return updated;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const updatedPayload = {
                workTypeIds: selectedWorkTypes.map(w => w.value),
                employeeIds: selectedEmployees.map(e => e.value),
                inventoryItems: selectedInventory.map(inv => ({
                    id: inv.value,
                    quantity: inventoryDetails[inv.value]?.quantity || 1,
                    underWarranty: inventoryDetails[inv.value]?.underWarranty || false
                }))
            };

            await updateJobCardForWorkshopId(workshopId, jobCardId, updatedPayload);
            onUpdate();
            handleClose();
        } catch (err) {
            console.error("Update failed:", err);
            showNotification(err.response.data.message, "error")
        } finally {
            setSaving(false);
        }
    };

    if (!show) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={handleClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-lg shadow-lg w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 p-6 max-h-[90vh] overflow-y-auto"
            >
                <div className="flex justify-between items-center border-b pb-4">
                    <h2 className="text-xl font-semibold text-blue-600 flex items-center">
                        <FaTools className="mr-2" />
                        Edit Job Card
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        &times;
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center mt-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <form onSubmit={(e) => e.preventDefault()} className="space-y-6 mt-4">
                        {/* Vehicle Info */}
                        <div>
                            <h5 className="text-blue-600 font-semibold flex items-center">
                                <FaCar className="mr-2" />
                                Vehicle Information
                            </h5>
                            <hr className="my-2" />
                            <input
                                type="text"
                                value={jobCard?.vehicleNumber}
                                disabled
                                className="w-full p-2 border rounded-md bg-gray-100 text-gray-700"
                            />
                        </div>

                        {/* Work Types */}
                        <div>
                            <h5 className="text-blue-600 font-semibold flex items-center">
                                <FaTools className="mr-2" />
                                Work Types
                            </h5>
                            <hr className="my-2" />
                            <Select
                                isMulti
                                options={workTypesOptions}
                                value={selectedWorkTypes}
                                onChange={(selected) => setSelectedWorkTypes(selected || [])}
                                placeholder="Search and select work types..."
                            />
                        </div>

                        {/* Assign Employees */}
                        <div>
                            <h5 className="text-blue-600 font-semibold flex items-center">
                                <FaUserTie className="mr-2" />
                                Assign Employees
                            </h5>
                            <hr className="my-2" />
                            <Select
                                isMulti
                                options={employeeOptions}
                                value={selectedEmployees}
                                onChange={(selected) => setSelectedEmployees(selected || [])}
                                placeholder="Search and select employees..."
                            />
                        </div>

                        {/* Inventory Items */}
                        <div>
                            <h5 className="text-blue-600 font-semibold flex items-center">
                                <FaBox className="mr-2" />
                                Inventory Items Used
                            </h5>
                            <hr className="my-2" />
                            <Select
                                isMulti
                                options={inventoryOptions}
                                value={selectedInventory}
                                onChange={handleInventoryChange}
                                placeholder="Search and select inventory items..."
                            />
                            {selectedInventory.map(item => (
                                <div key={item.value} className="flex items-center mt-2 space-x-4">
                                    <span className="text-sm w-1/3">{item.label}</span>
                                    <input
                                        type="number"
                                        className="w-20 border rounded p-1"
                                        value={inventoryDetails[item.value]?.quantity || 1}
                                        onChange={(e) =>
                                            setInventoryDetails(prev => ({
                                                ...prev,
                                                [item.value]: {
                                                    ...prev[item.value],
                                                    quantity: parseInt(e.target.value) || 1,
                                                },
                                            }))
                                        }
                                    />
                                    {inventoryDetails[item.value]?.warrantyType !== undefined &&
                                    <label className="text-sm flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={inventoryDetails[item.value]?.underWarranty || false}
                                            onChange={(e) =>
                                                setInventoryDetails(prev => ({
                                                    ...prev,
                                                    [item.value]: {
                                                        ...prev[item.value],
                                                        underWarranty: e.target.checked,
                                                    },
                                                }))
                                            }
                                            className="mr-1"
                                        />
                                        Under Warranty
                                    </label>
                                    }
                                </div>
                            ))}
                        </div>

                        {/* Submit */}
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
                        >
                            {saving ? (
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Saving...
                                </div>
                            ) : (
                                "Update Job Card"
                            )}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default EditJobCardModal;
